import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Phone, ChevronDown, ShieldCheck, Clock, Award } from "lucide-react";
import { Link } from "react-router-dom";
import TypewriterCycle from "@/components/TypewriterCycle";
import { SITE } from "@/data/site";

// Three rotating two-line headlines for the hero.
const HEADLINES = [
  ["Crystal clear pools.", "Zero hassle service."],
  ["Sparkling water.", "Stress-free summers."],
  ["Green to clean.", "Fast, fair, friendly."],
];

function MobileHero() {
  return (
    <section
      className="relative w-full overflow-hidden bg-gradient-to-b from-sky-50 via-white to-sky-50/40 pt-40 pb-8 sm:hidden"
      data-testid="hero-section-mobile"
    >
      <div className="relative z-10 px-5 flex flex-col items-center text-center">
        <h1
          className="font-heading text-[clamp(1.75rem,7vw,3rem)] font-extrabold leading-[1.05] tracking-tight text-slate-900"
          data-testid="hero-heading"
        >
          <TypewriterCycle
            lines={HEADLINES}
            line1ClassName="text-slate-900"
            line2ClassName="bg-gradient-to-r from-sky-700 via-sky-600 to-cyan-500 bg-clip-text text-transparent"
          />
        </h1>

        {/* Contained video — visible above the CTAs */}
        <div className="mt-5 w-full rounded-3xl overflow-hidden border border-white shadow-[0_20px_60px_rgba(10,37,64,0.18)] bg-[#0a2540]">
          <video
            src="/hero-video.mp4"
            poster="/hero-video-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="w-full h-auto block aspect-video object-contain bg-[#0a2540]"
            data-testid="hero-video-mobile"
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mt-5 flex flex-row flex-wrap justify-center gap-2.5"
        >
          <Link
            to="/contact"
            data-testid="hero-cta-quote"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-amber-400 to-amber-300 text-slate-900 font-bold text-sm shadow-lg shadow-amber-500/30"
          >
            Get a Free Quote
            <ChevronDown className="w-4 h-4 -rotate-90" />
          </Link>
          <a
            href={`tel:${SITE.phoneTel}`}
            data-testid="hero-cta-call"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white text-slate-900 font-bold text-sm border border-slate-200"
          >
            <Phone className="w-4 h-4" />
            {SITE.phoneDisplay}
          </a>
        </motion.div>

        <motion.ul
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-5 flex flex-wrap justify-center gap-x-4 gap-y-1.5 text-[11px] text-slate-700 font-medium"
        >
          <li className="inline-flex items-center gap-1.5">
            <Award className="w-3.5 h-3.5 text-amber-600" /> {SITE.experience}
          </li>
          <li className="inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-sky-700" /> 24/7 emergency
          </li>
          <li className="inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" /> Licensed &amp; insured
          </li>
        </motion.ul>
      </div>

      <div className="absolute inset-x-0 bottom-0 z-[5] pointer-events-none">
        <svg viewBox="0 0 1440 90" preserveAspectRatio="none" className="w-full h-12 block" aria-hidden>
          <path fill="#ffffff">
            <animate
              attributeName="d"
              dur="9s"
              repeatCount="indefinite"
              values="
                M0 0 C 240 90 480 10 720 50 C 960 90 1200 10 1440 50 L 1440 90 L 0 90 Z;
                M0 0 C 240 30 480 90 720 40 C 960 10 1200 90 1440 30 L 1440 90 L 0 90 Z;
                M0 0 C 240 90 480 10 720 50 C 960 90 1200 10 1440 50 L 1440 90 L 0 90 Z
              "
            />
          </path>
        </svg>
      </div>
    </section>
  );
}

function DesktopBackdrop({ scrollProgress }) {
  const prefersReducedMotion = useReducedMotion();
  const sunY = useTransform(scrollProgress, [0, 1], ["0%", "16%"]);
  const cloudsX = useTransform(scrollProgress, [0, 1], ["-4%", "18%"]);
  const overlayOpacity = useTransform(scrollProgress, [0, 0.5], [0.85, 0.45]);

  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden>
      <video
        src="/hero-video.mp4"
        poster="/hero-video-poster.jpg"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: "center center" }}
      />
      <motion.div
        style={{ y: prefersReducedMotion ? 0 : sunY }}
        className="absolute right-[6%] top-[6%] w-44 h-44 rounded-full bg-amber-200/40 blur-3xl"
      />
      <motion.svg
        viewBox="0 0 800 200"
        style={{ x: prefersReducedMotion ? 0 : cloudsX }}
        className="absolute left-0 top-[3%] w-[140%] h-24 opacity-50 mix-blend-screen"
      >
        <g fill="#ffffff">
          <ellipse cx="120" cy="80" rx="70" ry="14" />
          <ellipse cx="480" cy="60" rx="80" ry="16" />
          <ellipse cx="720" cy="90" rx="60" ry="12" />
        </g>
      </motion.svg>
      <motion.div
        style={{ opacity: prefersReducedMotion ? 0.6 : overlayOpacity }}
        className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.95)_0%,rgba(255,255,255,0.7)_30%,rgba(255,255,255,0)_55%)]"
      />
    </div>
  );
}

export default function HeroAnimated() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  const [showChevron, setShowChevron] = useState(true);
  useEffect(() => {
    const onScroll = () => setShowChevron(window.scrollY < 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <MobileHero />

      <section
        ref={sectionRef}
        className="relative w-full overflow-hidden bg-sky-100 min-h-screen hidden sm:block"
        data-testid="hero-section"
      >
        <DesktopBackdrop scrollProgress={scrollYProgress} />

        <div className="relative z-10 max-w-7xl mx-auto px-10 pt-44 pb-32 flex flex-col min-h-[inherit]">
          <h1
            className="font-heading text-[clamp(2.4rem,6vw,4.8rem)] font-extrabold leading-[1.04] tracking-tight text-slate-900 max-w-4xl drop-shadow-[0_1px_0_rgba(255,255,255,0.6)]"
            data-testid="hero-heading-desktop"
          >
            <TypewriterCycle
              lines={HEADLINES}
              line1ClassName="text-slate-900"
              line2ClassName="bg-gradient-to-r from-sky-700 via-sky-600 to-cyan-500 bg-clip-text text-transparent"
            />
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mt-8 flex flex-row gap-4"
          >
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-gradient-to-r from-amber-400 to-amber-300 text-slate-900 font-bold shadow-lg shadow-amber-500/30 hover:-translate-y-0.5 transition-all"
            >
              Get a Free Quote
              <ChevronDown className="w-4 h-4 -rotate-90" />
            </Link>
            <a
              href={`tel:${SITE.phoneTel}`}
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white text-slate-900 font-bold border border-slate-200 hover:border-sky-500 hover:text-sky-700 hover:-translate-y-0.5 transition-all"
            >
              <Phone className="w-4 h-4" />
              Call {SITE.phoneDisplay}
            </a>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-800 font-medium"
          >
            <li className="inline-flex items-center gap-2">
              <Award className="w-4 h-4 text-amber-600" /> {SITE.experience} of pool experience
            </li>
            <li className="inline-flex items-center gap-2">
              <Clock className="w-4 h-4 text-sky-700" /> 24/7 emergency service
            </li>
            <li className="inline-flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-700" /> Local, friendly, dependable
            </li>
          </motion.ul>
        </div>

        {showChevron && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, y: [0, 8, 0] }}
            transition={{ delay: 1.2, duration: 1.6, repeat: Infinity }}
            className="absolute left-1/2 -translate-x-1/2 bottom-6 z-10 text-slate-700 text-xs tracking-[0.25em] uppercase flex flex-col items-center gap-2"
          >
            Scroll
            <ChevronDown className="w-4 h-4" />
          </motion.div>
        )}

        <div className="absolute inset-x-0 bottom-0 z-[5] pointer-events-none">
          <svg viewBox="0 0 1440 90" preserveAspectRatio="none" className="w-full h-20 block" aria-hidden>
            <path fill="#ffffff">
              <animate
                attributeName="d"
                dur="9s"
                repeatCount="indefinite"
                values="
                  M0 0 C 240 90 480 10 720 50 C 960 90 1200 10 1440 50 L 1440 90 L 0 90 Z;
                  M0 0 C 240 30 480 90 720 40 C 960 10 1200 90 1440 30 L 1440 90 L 0 90 Z;
                  M0 0 C 240 90 480 10 720 50 C 960 90 1200 10 1440 50 L 1440 90 L 0 90 Z
                "
              />
            </path>
          </svg>
        </div>
      </section>
    </>
  );
}
